//GalleryPage.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { canDeletePhoto, deletePhoto, getUserUploadedPhotos } from '../services/cloudinary';
import Masonry from 'react-masonry-css';
// lightweight glyphs used instead of MaterialIcon
import '../styles/WeddingPhotoGallery.css';

const GalleryPage = () => {
  const { websiteId, sectionId } = useParams();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    const loadPhotos = async () => {
      try {
        const websiteRef = doc(db, 'websites', websiteId);
        const websiteDoc = await getDoc(websiteRef);

        if (websiteDoc.exists()) {
          const websiteData = websiteDoc.data();
          console.log('Fetched website data:', websiteData);
          const gallerySection = websiteData.sections?.find(
            (section) => section.id === sectionId && section.type === 'weddingPhotoGallery'
          );

          if (gallerySection) {
            const rawPhotos = gallerySection.props.uploadedPhotos || [];
            // Normalize photos to strings
            const normalizedPhotos = rawPhotos.map(photo =>
              typeof photo === 'string' ? photo : photo.secure_url || ''
            ).filter(url => url);
            setPhotos(normalizedPhotos);
            console.log('Normalized photos:', normalizedPhotos);
            console.log('Stored photos in localStorage:', getUserUploadedPhotos(websiteId, sectionId));
          } else {
            setError('Gallery not found');
          }
        } else {
          setError('Website not found');
        }
      } catch (error) {
        console.error('Error loading gallery:', error);
        setError('Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [websiteId, sectionId]);

  const handleDeletePhoto = async (photoUrl) => {
    if (!canDeletePhoto(photoUrl, websiteId, sectionId)) {
      alert('Можете да изтриете само снимки, които сте качили вие.');
      return;
    }

    if (!window.confirm('Сигурни ли сте, че искате да изтриете тази снимка?')) {
      return;
    }

    setDeleting(photoUrl);

    try {
      console.log('Attempting to delete image:', photoUrl);
      await deletePhoto(photoUrl, websiteId, sectionId);

      // Update Firebase
      const websiteRef = doc(db, 'websites', websiteId);
      const websiteDoc = await getDoc(websiteRef);

      if (websiteDoc.exists()) {
        const websiteData = websiteDoc.data();
        const sectionIndex = websiteData.sections?.findIndex(
          (section) => section.id === sectionId && section.type === 'weddingPhotoGallery'
        );

        if (sectionIndex !== -1) {
          const updatedSections = [...websiteData.sections];
          const currentPhotos = updatedSections[sectionIndex].props.uploadedPhotos || [];
          const updatedPhotos = currentPhotos.filter(photo => {
            const url = typeof photo === 'string' ? photo : photo.secure_url || '';
            return url !== photoUrl;
          });
          updatedSections[sectionIndex].props.uploadedPhotos = updatedPhotos;

          await updateDoc(websiteRef, {
            sections: updatedSections
          });

          const normalizedPhotos = updatedPhotos.map(photo =>
            typeof photo === 'string' ? photo : photo.secure_url || ''
          ).filter(url => url);
          setPhotos(normalizedPhotos);
        } else {
          throw new Error('Section not found');
        }
      } else {
        throw new Error('Website not found');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Грешка при изтриване на снимката. Моля, опитайте отново.');
    } finally {
      setDeleting(null);
    }
  };

  const breakpointColumns = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1
  };

  if (loading) return <div className="website-viewer-loading"><div className="spinner"></div><p>Зареждане...</p></div>;
  if (error) return <div className="website-viewer-error"><h2>Error</h2><p>{error}</p></div>;

  return (
    <div className="wedding-photo-gallery">
      <div className="gallery-header">
        <h2 className="gallery-title">
          Качени снимки
          <div className="title-underline" />
        </h2>
        <p className="gallery-description">
          Вижте всички снимки качени от гостите
        </p>
      </div>

      {photos.length > 0 ? (
        <Masonry
          breakpointCols={breakpointColumns}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >
          {photos.map((photo, index) => (
            <div key={index} className="photo-item">
              <img
                src={photo}
                alt={`Качена снимка ${index + 1}`}
                className="photo-image"
                onError={(e) => {
                  console.error('Failed to load image:', { url: photo, error: e.target.error });
                  e.target.src = '/path/to/fallback-image.jpg'; // Replace with actual fallback image
                }}
                onLoad={() => console.log('Image loaded successfully:', photo)}
              />
              {canDeletePhoto(photo, websiteId, sectionId) && (
                <button
                  className={`delete-photo-button ${deleting === photo ? 'deleting' : ''}`}
                  onClick={() => handleDeletePhoto(photo)}
                  disabled={deleting === photo}
                  title="Изтрий снимка"
                >
                  {deleting === photo ? <div className="delete-spinner"></div> : '×'}
                </button>
              )}
            </div>
          ))}
        </Masonry>
      ) : (
        <p className="gallery-description">Все още няма качени снимки.</p>
      )}
    </div>
  );
};

export default GalleryPage;
